// Successor Path curriculum. Plain fifth-grade voice. No em/en dashes.
// No use of "template" in member copy. First-use glosses live in-line.

export interface LearnCard {
  key: string; // L1..L7
  title: string;
  body: string[]; // paragraphs
  prompt: string;
}

export interface WitnessItem {
  key: string; // W1..W3
  title: string;
  body: string;
  prompt: string;
}

export interface PracticeItem {
  key: string; // P1..P3
  title: string;
  body: string;
  prompt: string;
}

export const LEARN_CARDS: LearnCard[] = [
  {
    key: "L1",
    title: "You care for it. You do not own it.",
    body: [
      "The Association owns the things it holds. You do not own them.",
      "As a successor, you will be given a job and a duty to serve. You will not be given money or a title of your own.",
      "This is a role of care, given to you in trust.",
    ],
    prompt: "Say in your own words what it means to care for something you do not own.",
  },
  {
    key: "L2",
    title: "The teaching, in short",
    body: [
      "God owns all things. We serve as helpers who care for them.",
      "The Association is here to live that teaching out in a real, day to day way.",
      "Everything else on this path grows from those two ideas.",
    ],
    prompt: "Say the teaching back in your own words.",
  },
  {
    key: "L3",
    title: "The two main jobs",
    body: [
      "The Principal Executive Officer leads and does the day to day work.",
      "The Secretary Treasurer keeps the notes and the money records.",
      "Each job has clear duties written down. The job is bigger than any one person.",
    ],
    prompt: "Which job will you take, and what is its main duty?",
  },
  {
    key: "L4",
    title: "The record is the real work",
    body: [
      "Every choice gets a meeting.",
      "Every meeting makes a resolution, a short paper that says what the Board (the group of officers) decided.",
      "Every resolution goes into the Record, our book of what we did. If it is not in the Record, it did not happen.",
    ],
    prompt: "Why do we say the record is the real work?",
  },
  {
    key: "L5",
    title: "The lines we do not cross",
    body: [
      "Your own money and the Association's money never mix. Not even once.",
      "Any time an officer would gain something, we test it and write it down first.",
      "No thing moves during any dispute. If a dispute is going on, we stop and we call a licensed helper.",
    ],
    prompt: "Name one line you will keep, and how you will keep it.",
  },
  {
    key: "L6",
    title: "Your power is given, never taken",
    body: [
      "A steward acts only within the rules of the Constitution and its resolutions.",
      "When it is not clear if you are allowed to do something, put it on paper first.",
      "The paper is not a delay. The paper is how the job speaks.",
    ],
    prompt: "What will you do next time an action is not clearly allowed?",
  },
  {
    key: "L7",
    title: "Keys and logins come at the right moment",
    body: [
      "Logins and keys come to you through set steps, at set times.",
      "They never come early and they never come by a quick text or word of mouth.",
      "Never write down or share secrets inside this portal.",
    ],
    prompt: "Say the rule about keys and logins in your own words.",
  },
];

export const WITNESS_ITEMS: WitnessItem[] = [
  {
    key: "W1",
    title: "Read the Constitution with the steward",
    body: "Sit down with the current steward. Read your Association's Constitution from start to end. Ask questions. Mark parts you want to come back to later.",
    prompt: "When did you do this, and what stood out to you?",
  },
  {
    key: "W2",
    title: "Sit in on one real meeting",
    body: "Be there for one real meeting of the Association. Watch how it opens, how choices are named, and how the minutes (the notes) are kept.",
    prompt: "Which meeting did you sit in on, and what did you notice?",
  },
  {
    key: "W3",
    title: "Tour the Record together",
    body: "Walk through the Record with the steward. See where resolutions live, where signed papers live, and how to find any part of it later.",
    prompt: "Say in one or two sentences what the Record is like.",
  },
];

export const PRACTICE_ITEMS: PracticeItem[] = [
  {
    key: "P1",
    title: "Draft one practice resolution together",
    body: "With the steward, draft one practice resolution from start to end. Use a real sample from the library as your starting point.",
    prompt: "What was the resolution about, and what did you learn from writing it?",
  },
  {
    key: "P2",
    title: "Walk one key or login path as a drill",
    body: "Pick one login or key path from the plan. Walk it as a drill with the steward next to you. Do not share or write down any secret.",
    prompt: "Which path did you walk, and what would you make better about it?",
  },
  {
    key: "P3",
    title: "Write what the mission means to you",
    body: "Write a short note on what the mission of this Association means to you, and how you plan to serve it.",
    prompt: "Write your note here. A short paragraph is plenty.",
  },
];

// The seven promises the successor makes at the Capstone. Plain and personal.
export const SEVEN_UNDERSTANDINGS: string[] = [
  "I care for what the Association holds. I do not own it. I will take a job and a duty, not money or a title.",
  "The mission comes first. The Association's things serve its work, not mine.",
  "The record is the real work. Every choice gets a meeting, a short paper (a resolution), and a place in the Record.",
  "The lines hold me. My money and the Association's money never mix, not even once. Any time I would gain something, we test it and write it down first.",
  "My power is given, not taken. I act inside the rules. When I am not sure I am allowed, I put it on paper first.",
  "Keys and logins come to me through set steps, at set times. Not early. Not by text or word of mouth.",
  "I do not do this alone. Licensed helpers, our people, and the portal each carry their part. When something belongs with a helper, I send it there.",
];

export const CAPSTONE_KEY = "SU_ACK";

export type AttestationStage = "learn" | "witness" | "practice";

export interface AttestationRow {
  id: string;
  successor_id: string;
  stage: AttestationStage;
  item_key: string;
  successor_response: string | null;
  successor_confirmed_at: string | null;
  chair_confirmed_by: string | null;
  chair_confirmed_at: string | null;
}
